// Copyright (c) 2019 embyt GmbH. All rights reserved.
// Author: Roman Morawek

import * as React from "react";
import { connect } from "react-redux";
import { defineMessages, IntlContext, IntlShape } from "react-intl";
import { Link } from "react-router-dom";

import { isSet } from "shared/utils/utils";
import { Bus, IInput, IModule, InputType } from "shared/types/parameter";
import { ICloudParameter } from "shared/types/cloudtypes";
import { MainPanel } from "types";
import { IState } from "states/states";
import { ActionType, IAction, IActionChangeInput, raiseAction } from "actions";
import {
  makeCommandSeqNr,
  tabClickHandler,
  getVirtualSiteSourceName,
  getModuleChannelName,
  renderVirtualSiteIcon,
} from "guiutils";

import InputModel, { IOState } from "model/input";

import IconOff from "icons/iconOff";
import IconOn from "icons/iconOn";
import IconClock from "icons/iconClock";
import IconUnknown from "icons/iconUnknown";
import IconEdit from "icons/iconEdit";

interface IInputExtProps {
  index: number;
  isEditable: boolean;
  showModule: boolean;
  isDashboard?: boolean;
}

interface IInputProps {
  parameter: IInput;
  rawValue: number | undefined;
  timestamp: number;
  module: IModule | undefined;
  moduleStatus: boolean | undefined;
  showId: boolean;
  virtualSiteName: string | undefined;
}

const mapStateToProps = (state: IState, ownProps: IInputExtProps) => {
  const parameter = state.para.parameter!.input[ownProps.index];
  const moduleIndex =
    parameter?.bus === Bus.MODULE ?
      state.para.parameter?.module?.findIndex((curItem) => curItem.id === parameter.module)
    : undefined;
  const curUser = state.site.userList?.find((curItem) => curItem.secId === state.site.user?.secId);
  const props: IInputProps = {
    parameter,
    rawValue: state.data.input[ownProps.index],
    timestamp: state.data.timestamp,
    module: isSet(moduleIndex) ? state.para.parameter?.module?.[moduleIndex] : undefined,
    moduleStatus: isSet(moduleIndex) ? state.data.module[moduleIndex] : true,
    showId: !ownProps.isDashboard && Boolean(curUser?.accessRights.setVirtualParameters),
    virtualSiteName:
      ownProps.isDashboard ? undefined : (
        getVirtualSiteSourceName(
          (state.para.parameter as ICloudParameter | undefined)?.virtualSite?.input,
          ownProps.index,
          state.cached.selectedSite,
          state.site.siteList,
        )
      ),
  };
  return props;
};

interface IInputDispatch {
  onEdit: () => void;
  onChange: (value: number) => void;
}

const mapDispatchToProps = (dispatch: (action: IAction<any>) => void, ownProps: IInputExtProps) => {
  const dispatchProps: IInputDispatch = {
    onEdit: () => {
      dispatch(raiseAction(ActionType.ACT_EDIT_INPUT, ownProps.index));
    },
    onChange: (value: number) => {
      const payload: IActionChangeInput = {
        commandSeqNr: makeCommandSeqNr(),
        index: ownProps.index,
        value,
      };
      dispatch(raiseAction(ActionType.ACT_CHANGE_INPUT_REQ, payload));
    },
  };
  return dispatchProps;
};

const messages = defineMessages({
  msgEdit: {
    id: "change value",
  },
  ioType: {
    id: "Inputs",
  },
});

// messages with placeholder values
const valueMessages = defineMessages<{
  virtualSite: { site: string };
}>({
  virtualSite: {
    id: "This item's data originates from {site}",
  },
});

class InputComponent extends React.Component<IInputProps & IInputExtProps & IInputDispatch> {
  static contextType = IntlContext;
  declare context: IntlShape;

  public render() {
    const input = new InputModel(
      this.props.parameter,
      this.props.rawValue,
      this.props.timestamp,
      this.props.moduleStatus,
    );
    const hasValue = input.parameter.type === InputType.ANALOG;
    const isEditable =
      this.props.isEditable && input.parameter.webCtrl && this.isChangeable() && !input.isOffline;

    return (
      <tr className={`inputitem ${input.isOffline ? "offline" : ""}`}>
        {this.props.showId && <td className="optional-md">{this.props.parameter.id}</td>}
        <td>
          {this.props.parameter.name}
          {this.props.virtualSiteName &&
            renderVirtualSiteIcon(
              this.context.formatMessage(valueMessages.virtualSite, {
                site: this.props.virtualSiteName,
              }),
            )}
        </td>
        {this.props.showModule && (
          <td className="optional-sm">
            <Link to={`../${MainPanel.MODULES}`} style={{ textDecoration: "none" }}>
              {this.props.module?.name}
              {this.props.module &&
                ` - ${getModuleChannelName(this.props.module.type, this.props.parameter.channel, "input")}`}
            </Link>
          </td>
        )}
        <td
          className={`icon-col ${hasValue ? "" : "right"} ${isEditable ? "img-link" : ""}`}
          colSpan={hasValue ? 1 : 2}
          title={input.getText()}
          onClick={
            isEditable ?
              input.parameter.type === InputType.ANALOG ?
                this.props.onEdit
              : () => {
                  this.props.onChange(input.state === IOState.OFF ? 1 : 0);
                }

            : undefined
          }
        >
          {this.getIcon(input)}
        </td>
        {hasValue && (
          <td className="value-col">
            {input.parameter.type === InputType.ANALOG && input.getText()}
          </td>
        )}
        {isEditable ?
          <td
            tabIndex={0}
            onKeyDown={tabClickHandler}
            onClick={this.props.onEdit}
            className="edit-col img-link"
            title={this.context.formatMessage(messages.msgEdit)}
          >
            <IconEdit />
          </td>
        : <td className="edit-col"></td>}
      </tr>
    );
  }

  private getIcon(input: InputModel) {
    if (input.parameter.type === InputType.ANALOG) {
      return input.parameter.unit && ["date", "time", "timestamp"].includes(input.parameter.unit) ?
          <IconClock />
        : <></>;
    }
    if (input.state === IOState.OFF) return <IconOff />;
    if (input.state === IOState.ON) return <IconOn />;
    return <IconUnknown />;
  }

  private isChangeable() {
    return this.props.parameter.bus === Bus.VIRTUAL;
  }
}

export default connect(mapStateToProps, mapDispatchToProps)(InputComponent);
